/**
 * Trust, quality and provenance components.
 *
 * This file is where the product's honesty lives. Every element here exists to
 * answer "how do you know that?" without the user having to ask, and each one
 * refuses to overstate:
 *
 * - Two scores are shown side by side and never averaged (§30, §31).
 * - A fact, an inference and an estimate are rendered differently (§54).
 * - Every price and claim shows its source and its age (§55).
 * - "Unverified" is stated neutrally — the absence of corroboration is a fact
 *   about our evidence, not an accusation against the business (§31).
 */

import type { ReactNode } from 'react'
import {
  AlertIcon, ClockIcon, InfoIcon, LinkIcon, ShieldCheckIcon, SourceIcon,
} from '@/components/ui/icons'
import { Badge, cx } from '@/components/ui/primitives'
import {
  FRESHNESS_LABEL, formatDate, formatRelative, displayUrl, freshnessOf, humanise,
} from '@/utils/format'
import type {
  AssertionType, Fact, ScoreBreakdown, SourceRef, TrustBand, VerificationStatus,
} from '@/types/api'

/* -- Verification ---------------------------------------------------------- */
const VERIFICATION_TONE = {
  officially_verified: 'official',
  verified: 'verified',
  partially_verified: 'caution',
  unverified: 'neutral',
} as const

const VERIFICATION_LABEL: Record<VerificationStatus, string> = {
  officially_verified: 'Officially verified',
  verified: 'Verified',
  partially_verified: 'Partly verified',
  unverified: 'Not yet verified',
}

export function VerificationBadge({
  status, size = 'sm',
}: {
  status: VerificationStatus
  size?: 'xs' | 'sm'
}) {
  const verified = status === 'verified' || status === 'officially_verified'
  return (
    <Badge
      tone={VERIFICATION_TONE[status]}
      size={size}
      icon={verified ? <ShieldCheckIcon size={size === 'xs' ? 10 : 12} /> : undefined}
      // Deliberately neutral wording. "Not yet verified" says something about
      // our evidence; "unverified business" would read as a judgement about
      // the company, which the data does not support.
      title={
        verified
          ? 'Contact details or registration have been confirmed against evidence.'
          : 'We have not yet confirmed this business against independent evidence.'
      }
    >
      {VERIFICATION_LABEL[status]}
    </Badge>
  )
}

/* -- Scores ---------------------------------------------------------------- */
const TRUST_BAND_TONE: Record<TrustBand, string> = {
  well_corroborated: 'var(--color-positive)',
  corroborated: 'var(--color-verified)',
  limited_corroboration: 'var(--color-caution)',
  minimal_corroboration: 'var(--color-unverified)',
}

/**
 * A single score, drawn as a meter.
 *
 * The meter matters more than the number: "62" means nothing on its own, but a
 * bar two-thirds full against a track communicates immediately.
 */
export function ScoreMeter({
  label, score, band, tone, description, className,
}: {
  label: string
  score: number
  band?: TrustBand
  tone?: string
  description?: string
  className?: string
}) {
  const colour = tone ?? (band ? TRUST_BAND_TONE[band] : 'var(--accent)')
  return (
    <div className={className}>
      <div className="flex items-baseline justify-between gap-2">
        <span className="field-label">{label}</span>
        <span className="text-sm font-semibold tabular" style={{ color: colour }}>
          {score}
          <span className="text-xs font-normal text-[var(--text-tertiary)]">/100</span>
        </span>
      </div>
      <div
        className="mt-1.5 h-1.5 overflow-hidden rounded-full bg-[var(--surface-sunken)]"
        role="meter"
        aria-valuenow={score}
        aria-valuemin={0}
        aria-valuemax={100}
        aria-label={label}
      >
        <div
          className="h-full rounded-full transition-[width] duration-500"
          style={{ width: `${Math.max(2, Math.min(100, score))}%`, background: colour }}
        />
      </div>
      {description ? (
        <p className="mt-1 text-xs text-[var(--text-tertiary)]">{description}</p>
      ) : null}
    </div>
  )
}

/**
 * Both scores, together, with the distinction spelled out.
 *
 * They are shown as a pair on purpose. Seen alone, either invites the wrong
 * conclusion: a high quality score looks like an endorsement, and a low trust
 * score looks like a warning. Side by side they say what they actually mean —
 * "we know a lot about this, and little of it is confirmed."
 */
export function ScorePair({
  quality, trust, className, stacked = false,
}: {
  quality: ScoreBreakdown
  trust: ScoreBreakdown
  className?: string
  /** Stack instead of sitting side by side — for narrow sidebar panels, where
   *  two columns clip the "/100" off the end of the longer label. */
  stacked?: boolean
}) {
  return (
    <div className={cx(stacked ? 'space-y-4' : 'grid gap-4 sm:grid-cols-2', className)}>
      <ScoreMeter
        label="Data quality"
        score={quality.score}
        tone="var(--color-info)"
        description="How complete this record is."
      />
      <ScoreMeter
        label="Corroboration"
        score={trust.score}
        band={trust.band}
        description="How much independent evidence supports it."
      />
    </div>
  )
}

/** The component-by-component breakdown. A score you cannot interrogate is a badge. */
export function ScoreBreakdownList({ breakdown }: { breakdown: ScoreBreakdown }) {
  return (
    <ul className="divide-hairline">
      {breakdown.components.map((component) => (
        <li key={component.key} className="flex items-center gap-3 py-2">
          <span
            className={cx(
              'flex size-4 shrink-0 items-center justify-center rounded-full text-2xs',
              component.passed
                ? 'bg-[color-mix(in_oklch,var(--color-positive)_18%,transparent)] text-[var(--color-positive)]'
                : 'bg-[var(--surface-sunken)] text-[var(--text-tertiary)]',
            )}
            aria-hidden="true"
          >
            {component.passed ? '✓' : '·'}
          </span>
          <span className="min-w-0 flex-1 text-sm text-[var(--text-primary)]">
            {component.label}
            {component.detail ? (
              <span className="ml-1.5 text-xs text-[var(--text-tertiary)]">
                {component.detail}
              </span>
            ) : null}
          </span>
          <span className="shrink-0 text-xs tabular text-[var(--text-secondary)]">
            {component.earned}/{component.weight}
          </span>
        </li>
      ))}
    </ul>
  )
}

/* -- Provenance ------------------------------------------------------------ */
/**
 * The chip that appears next to any sourced value — spec §55.
 *
 * Domain, age and a confidence dot, in the smallest space that can carry all
 * three. The link opens in a new tab with `noopener`: it points at a crawled
 * third-party site, and `window.opener` access is not something to hand out.
 */
export function SourceChip({
  url, domain, observedAt, confidence, className,
}: {
  url?: string | null
  domain?: string | null
  observedAt?: string | null
  confidence?: number
  className?: string
}) {
  if (!url && !domain) return null

  const label = domain ?? displayUrl(url, 28)
  const body = (
    <>
      <SourceIcon size={11} className="shrink-0 opacity-70" />
      <span className="truncate">{label}</span>
      {observedAt ? (
        <span className="shrink-0 text-[var(--text-tertiary)]">
          · {formatRelative(observedAt)}
        </span>
      ) : null}
      {confidence !== undefined ? <ConfidenceDot value={confidence} /> : null}
    </>
  )

  const classes = cx(
    'inline-flex max-w-full items-center gap-1 rounded-full border border-[var(--border-subtle)]',
    'bg-[var(--surface-sunken)] px-2 py-0.5 text-2xs text-[var(--text-secondary)]',
    url && 'hover:border-[var(--accent-border)] hover:text-[var(--accent-text)] transition-colors',
    className,
  )

  if (!url) return <span className={classes}>{body}</span>

  return (
    <a
      href={url}
      target="_blank"
      rel="noopener noreferrer nofollow"
      className={classes}
      title={`Source: ${url}`}
    >
      {body}
    </a>
  )
}

/**
 * Confidence as three filled dots.
 *
 * Chosen over a percentage because 0.63 implies a precision the scoring does
 * not have. Three levels is what the data can actually support.
 */
export function ConfidenceDot({ value }: { value: number }) {
  const level = value >= 0.8 ? 3 : value >= 0.5 ? 2 : 1
  const colour =
    level === 3 ? 'var(--color-positive)'
    : level === 2 ? 'var(--color-caution)'
    : 'var(--color-unverified)'
  const label = level === 3 ? 'High' : level === 2 ? 'Medium' : 'Low'

  return (
    <span
      className="ml-0.5 inline-flex shrink-0 items-center gap-px"
      title={`${label} confidence (${Math.round(value * 100)}%)`}
      aria-label={`${label} confidence`}
    >
      {[1, 2, 3].map((index) => (
        <span
          key={index}
          className="size-1 rounded-full"
          style={{
            background: index <= level ? colour : 'var(--border-default)',
          }}
        />
      ))}
    </span>
  )
}

/** How recently something was checked. Prices in Nigeria move. */
export function FreshnessTag({
  days, observedAt, className,
}: {
  days?: number | null
  observedAt?: string | null
  className?: string
}) {
  const computed =
    days ??
    (observedAt
      ? Math.floor((Date.now() - new Date(observedAt).getTime()) / 86_400_000)
      : null)
  const state = freshnessOf(computed)

  const tone =
    state === 'fresh' ? 'positive'
    : state === 'recent' ? 'neutral'
    : state === 'ageing' ? 'caution'
    : state === 'stale' ? 'stale'
    : 'neutral'

  return (
    <Badge
      tone={tone}
      size="xs"
      className={className}
      icon={<ClockIcon size={10} />}
      title={observedAt ? `Last observed ${formatDate(observedAt)}` : undefined}
    >
      {state === 'unknown'
        ? FRESHNESS_LABEL.unknown
        : `${FRESHNESS_LABEL[state]} · ${computed}d`}
    </Badge>
  )
}

/* -- Facts, inferences and estimates — spec §54 ----------------------------- */
const ASSERTION_META: Record<
  AssertionType,
  { tone: 'fact' | 'inference' | 'estimate'; label: string; note: string; icon: ReactNode }
> = {
  fact: {
    tone: 'fact',
    label: 'Fact',
    note: 'Stated directly by a source we can link to.',
    icon: <LinkIcon size={11} />,
  },
  inference: {
    tone: 'inference',
    label: 'Inference',
    note: 'Derived from evidence. Not stated outright by any source.',
    icon: <InfoIcon size={11} />,
  },
  estimate: {
    tone: 'estimate',
    label: 'Estimate',
    note: 'A modelled figure. Treat as indicative, not as a measurement.',
    icon: <AlertIcon size={11} />,
  },
}

export function AssertionBadge({ type }: { type: AssertionType }) {
  const meta = ASSERTION_META[type]
  return (
    <Badge tone={meta.tone} size="xs" icon={meta.icon} title={meta.note}>
      {meta.label}
    </Badge>
  )
}

/**
 * One group of claims of a single kind.
 *
 * Grouping is the point. A profile that interleaves "employs 500 people" with
 * "appears to be a distributor" and "estimated 4% market share" reads as three
 * equally solid statements, and only one of them is.
 */
export function FactGroup({
  type, facts,
}: {
  type: AssertionType
  facts: Fact[]
}) {
  if (facts.length === 0) return null
  const meta = ASSERTION_META[type]

  return (
    <section className="space-y-2">
      <div className="flex items-center gap-2">
        <AssertionBadge type={type} />
        <p className="text-xs text-[var(--text-tertiary)]">{meta.note}</p>
      </div>

      <dl className="divide-hairline rounded-lg border border-[var(--border-subtle)] px-3">
        {facts.map((fact) => (
          <div key={`${fact.key}-${fact.observed_at}`} className="py-2.5">
            <div className="flex items-baseline justify-between gap-3">
              <dt className="text-xs text-[var(--text-tertiary)]">{humanise(fact.key)}</dt>
              <dd className="text-right text-sm font-medium text-[var(--text-primary)] tabular">
                {fact.value}
                {fact.unit ? (
                  <span className="ml-1 text-xs font-normal text-[var(--text-tertiary)]">
                    {fact.unit}
                  </span>
                ) : null}
              </dd>
            </div>

            {/* An unexplained inference is indistinguishable from a guess, so
                the reasoning is shown rather than tucked into a tooltip. */}
            {fact.reasoning && type !== 'fact' ? (
              <p className="mt-1 text-xs italic text-[var(--text-secondary)]">
                {fact.reasoning}
              </p>
            ) : null}

            <div className="mt-1.5 flex flex-wrap items-center gap-1.5">
              {/* The source and the date it was read stay — they are the
                  claim's provenance. How our pipeline happened to read it
                  ("metadata", "demo") is internal jargon that tells a reader
                  nothing they can check, so it is not shown. */}
              <SourceChip
                url={fact.source_url}
                observedAt={fact.observed_at}
                confidence={fact.confidence}
              />
            </div>
          </div>
        ))}
      </dl>
    </section>
  )
}

/* -- Source list ----------------------------------------------------------- */
export function SourceList({ sources }: { sources: SourceRef[] }) {
  if (sources.length === 0) {
    return (
      <p className="text-sm text-[var(--text-secondary)]">
        No sources recorded yet. Nothing on this profile has been published.
      </p>
    )
  }

  return (
    <ul className="divide-hairline">
      {sources.map((source) => (
        <li key={source.url} className="flex items-start gap-3 py-2.5">
          <SourceIcon
            size={14}
            className={cx(
              'mt-0.5 shrink-0',
              source.is_active ? 'text-[var(--accent)]' : 'text-[var(--text-tertiary)]',
            )}
          />
          <div className="min-w-0 flex-1">
            <a
              href={source.url}
              target="_blank"
              rel="noopener noreferrer nofollow"
              className="block truncate text-sm text-[var(--text-primary)] hover:text-[var(--accent-text)]"
            >
              {source.title ?? displayUrl(source.url, 60)}
            </a>
            <p className="mt-0.5 flex flex-wrap items-center gap-x-2 gap-y-0.5 text-2xs text-[var(--text-tertiary)]">
              <span>{source.domain}</span>
              <span>·</span>
              <span>{humanise(source.type)}</span>
              <span>·</span>
              <span>checked {formatRelative(source.last_seen_at)}</span>
              {/* A source that stopped mentioning the business is retained —
                  "this used to say so" is itself information. */}
              {!source.is_active ? (
                <Badge tone="neutral" size="xs">
                  No longer found
                </Badge>
              ) : null}
            </p>
          </div>
          <ConfidenceDot value={source.confidence} />
        </li>
      ))}
    </ul>
  )
}
